import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { MessageCircle } from "lucide-react";

const ContactHero = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
  };

  return (
    <section
      ref={ref}
      className="relative min-h-[calc(100vh-4rem)] lg:min-h-[80vh] bg-cover bg-center bg-no-repeat overflow-hidden flex items-center py-12 sm:py-16 md:py-20 pt-16 sm:pt-20"
      style={{
        backgroundImage: "url(/main.png)",
      }}
    >
      {/* Dark overlay for better text readability */}
      <div className="absolute inset-0 bg-black/50"></div>

      {/* Floating decorative elements */}
      <motion.div
        className="absolute right-6 sm:right-10 top-1/4 w-24 sm:w-32 md:w-40 h-24 sm:h-32 md:h-40 rounded-full bg-gradient-to-br from-soft-saffron/20 to-copper-brown/20 blur-2xl sm:blur-3xl hidden sm:block"
        animate={{
          scale: [1, 1.3, 1],
          opacity: [0.3, 0.7, 0.3],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      <motion.div
        className="absolute left-6 sm:left-10 bottom-1/4 w-20 sm:w-24 md:w-32 h-20 sm:h-24 md:h-32 rounded-full bg-gradient-to-tr from-forest-green/20 to-misty-sage/20 blur-xl sm:blur-2xl hidden sm:block"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.2, 0.6, 0.2],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 2,
        }}
      />

      {/* Sanskrit Greeting - Enhanced */}
      <motion.div
        className="absolute top-12 sm:top-16 md:top-20 right-6 sm:right-10 text-4xl sm:text-5xl md:text-6xl lg:text-8xl text-soft-saffron/20 font-sanskrit hidden sm:block"
        animate={{
          y: [0, -10, 0],
          opacity: [0.2, 0.4, 0.2],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      >
        नमस्ते
      </motion.div>

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <div className="max-w-7xl mx-auto">
          {/* Main Content */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="text-center"
          >
            {/* Breadcrumb */}
            <motion.div variants={itemVariants} className="mb-4 sm:mb-6 md:mb-8">
              <div className="inline-flex items-center gap-2 px-3 sm:px-4 md:px-6 py-2 sm:py-3 rounded-full bg-cloud-white/10 border border-cloud-white/20 backdrop-blur-sm">
                <MessageCircle className="w-3 h-3 sm:w-4 sm:h-4 text-soft-saffron" />
                <span className="text-cloud-white/80 text-xs sm:text-sm">
                  <a
                    href="/"
                    className="hover:text-soft-saffron transition-colors drop-shadow-sm"
                  >
                    Home
                  </a>
                  <span className="mx-2">•</span>
                  <span className="text-soft-saffron">Contact</span>
                </span>
              </div>
            </motion.div>

            {/* Main Heading */}
            <motion.h1
              variants={itemVariants}
              className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-8xl font-bold text-cloud-white mb-3 sm:mb-4 md:mb-6 lg:mb-8 drop-shadow-2xl px-2"
            >
              Let's <span style={{ color: '#3FBA50' }}>Connect</span>
            </motion.h1>

            {/* Subheading */}
            <motion.p
              variants={itemVariants}
              className="text-base sm:text-lg md:text-xl lg:text-2xl text-cloud-white/90 leading-relaxed max-w-4xl mx-auto font-light drop-shadow-lg mb-6 sm:mb-8 md:mb-10 lg:mb-12 px-4 sm:px-6"
            >
              We're here to support your wellness journey. Reach out with
              questions, feedback, or collaboration ideas — we'd love to hear
              from you and be part of your path to inner peace.
            </motion.p>

            {/* Decorative divider */}
            <motion.div
              variants={itemVariants}
              className="flex items-center justify-center gap-2 sm:gap-3 md:gap-4"
            >
              <div className="w-8 sm:w-12 md:w-16 lg:w-20 h-[1px] sm:h-[2px] bg-gradient-to-r from-transparent to-soft-saffron"></div>
              <div className="w-2 h-2 sm:w-3 sm:h-3 md:w-4 md:h-4 rounded-full bg-soft-saffron"></div>
              <div className="w-8 sm:w-12 md:w-16 lg:w-20 h-[1px] sm:h-[2px] bg-gradient-to-l from-transparent to-soft-saffron"></div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactHero;
